/**
 * Conflict Resolver
 * 
 * Handles detection and resolution of conflicts between
 * offline actions and server state using updated_at timestamps.
 */
import { supabase } from '@/integrations/supabase/client';
import { PendingAction, updateAction, removeAction, OfflineActionType } from './action-queue';

export interface ConflictInfo {
  actionId: string;
  type: OfflineActionType;
  localUpdatedAt: string;
  serverUpdatedAt: string;
  payload: PendingAction['payload'];
}

export type ConflictResolution = 'overwrite' | 'discard';

/**
 * Check if an action has a conflict with server state
 * Returns conflict info if there's a conflict, null otherwise
 */
export async function checkForConflict(action: PendingAction): Promise<ConflictInfo | null> {
  const serverUpdatedAt = await getServerUpdatedAt(action);
  
  if (!serverUpdatedAt || !action.payload.updatedAt) {
    // Can't check conflict without timestamps - proceed with action
    return null;
  }

  const localTime = new Date(action.payload.updatedAt).getTime();
  const serverTime = new Date(serverUpdatedAt).getTime();

  // If server time is newer than our local timestamp, there's a conflict
  if (serverTime > localTime) {
    console.warn(
      `[ConflictResolver] Conflict detected for action ${action.id}: ` +
      `local=${action.payload.updatedAt}, server=${serverUpdatedAt}`
    );
    
    return {
      actionId: action.id,
      type: action.type,
      localUpdatedAt: action.payload.updatedAt,
      serverUpdatedAt,
      payload: action.payload,
    };
  }

  return null;
}

/**
 * Get the current updated_at timestamp from the server
 */
async function getServerUpdatedAt(action: PendingAction): Promise<string | null> {
  try {
    switch (action.type) {
      case 'UPDATE_INSTALLATION_STATUS': {
        if (!action.payload.installationId) return null;
        
        const { data, error } = await supabase
          .from('installations')
          .select('updated_at')
          .eq('id', action.payload.installationId)
          .single();
        
        if (error || !data) return null;
        return data.updated_at;
      }
      
      case 'UPDATE_LOAD_STATUS': {
        if (!action.payload.loadId) return null;
        
        const { data, error } = await supabase
          .from('installation_loads')
          .select('updated_at')
          .eq('id', action.payload.loadId)
          .single();
        
        if (error || !data) return null;
        return data.updated_at;
      }
      
      default:
        return null;
    }
  } catch (error) {
    console.error('[ConflictResolver] Error fetching server timestamp:', error);
    return null;
  }
}

/**
 * Resolve a conflict with the given resolution
 */
export async function resolveConflict(
  actionId: string, 
  resolution: ConflictResolution,
  action?: PendingAction
): Promise<void> {
  console.info(`[ConflictResolver] Resolving conflict for ${actionId}: ${resolution}`);
  
  switch (resolution) {
    case 'overwrite':
      // Update the action's updatedAt to current server time so it will pass conflict check
      if (action) {
        const serverUpdatedAt = await getServerUpdatedAt(action);
        if (serverUpdatedAt) {
          action.payload.updatedAt = serverUpdatedAt;
          action.actionStatus = 'pending'; // Reset to pending for re-sync
          await updateAction(action);
        }
      }
      break;
      
    case 'discard':
      // Simply remove the action from the queue
      await removeAction(actionId);
      break;
  }
}

/**
 * Get human-readable description of a conflict
 */
export function formatConflictDescription(conflict: ConflictInfo): string {
  switch (conflict.type) {
    case 'UPDATE_INSTALLATION_STATUS':
      return 'Asennuksen tiedot ovat muuttuneet palvelimella sen jälkeen kun teit muutoksen offline-tilassa.';
    case 'UPDATE_LOAD_STATUS':
      return 'Kuorman tiedot ovat muuttuneet palvelimella sen jälkeen kun teit muutoksen offline-tilassa.';
    default:
      return 'Tiedot ovat muuttuneet palvelimella.';
  }
}
